import StakeholderTxnsPanel from "@app/pages/stakeholders-merge/stakeholder-txns-panel";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { useEffect, useRef, useState } from "react";
import { commitMutation, fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { reviewPage_Query } from "@app/queries/reviewPage_Query.graphql";
import type { reviewPage_ConfirmSuggestionMutation } from "@app/queries/reviewPage_ConfirmSuggestionMutation.graphql";
import type { reviewPage_RejectSuggestionMutation } from "@app/queries/reviewPage_RejectSuggestionMutation.graphql";
import type { reviewPage_GenerateAiSuggestionsMutation } from "@app/queries/reviewPage_GenerateAiSuggestionsMutation.graphql";
import type { reviewPage_AssignStakeholderCategoryMutation } from "@app/queries/reviewPage_AssignStakeholderCategoryMutation.graphql";

// Suggestion rows carry a per-row matches_count (a description-match COUNT), so
// the list pages server-side — only the shown page pays that count.
export const PAGE_SIZE = 50;

export const reviewQuery = graphql`
  query reviewPage_Query($perspectiveId: ID, $limit: Int, $offset: Int) {
    categorizationCoverage {
      categorizedTxnsCount
      totalTxnsCount
    }
    pendingSuggestions(limit: $limit, offset: $offset) {
      totalCount
      suggestions {
        id
        pattern
        logic
        origin
        confidence
        rationale
        matchesCount
        totalAmount
        stakeholder {
          id
          name
        }
        category {
          id
          name
        }
      }
    }
    allStakeholders {
      id
      name
    }
    uncategorizedStakeholders(perspectiveId: $perspectiveId) {
      stakeholder {
        id
        name
      }
      totalSpend
    }
    categories(perspectiveId: $perspectiveId) {
      id
      name
      parent {
        name
      }
    }
  }
`;

const confirmMutation = graphql`
  mutation reviewPage_ConfirmSuggestionMutation(
    $input: ConfirmSuggestionInputObject!
  ) {
    confirmSuggestion(input: $input) {
      rule {
        id
      }
      coverage {
        categorizedTxnsCount
        totalTxnsCount
      }
      errors {
        message
      }
    }
  }
`;

const rejectMutation = graphql`
  mutation reviewPage_RejectSuggestionMutation(
    $input: RejectSuggestionInputObject!
  ) {
    rejectSuggestion(input: $input) {
      suggestion {
        id
        status
      }
      errors {
        message
      }
    }
  }
`;

const generateMutation = graphql`
  mutation reviewPage_GenerateAiSuggestionsMutation {
    generateAiSuggestions {
      enqueued
      errors {
        message
      }
    }
  }
`;

const assignCategoryMutation = graphql`
  mutation reviewPage_AssignStakeholderCategoryMutation(
    $input: AssignStakeholderCategoryInputObject!
  ) {
    assignStakeholderCategory(input: $input) {
      stakeholder {
        id
        category {
          id
          name
        }
      }
      errors {
        message
      }
    }
  }
`;

export default function ReviewPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<reviewPage_Query>;
}) {
  const preloaded = usePreloadedQuery(reviewQuery, queryRef);
  const [data, setData] = useState(preloaded);
  useEffect(() => setData(preloaded), [preloaded]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  const fetchPage = () => {
    setIsFetching(true);
    fetchQuery<reviewPage_Query>(environment, reviewQuery, {
      perspectiveId: getPerspectiveId() ?? undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    })
      .toPromise()
      .then((fresh) => fresh && setData(fresh))
      .finally(() => setIsFetching(false));
  };

  // Refetch on page change (skip first render — the loader has page 0).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const coverage = data.categorizationCoverage;
  const suggestions = data.pendingSuggestions.suggestions;
  const totalCount = data.pendingSuggestions.totalCount;
  const pct =
    coverage.totalTxnsCount === 0
      ? 100
      : Math.round(
          (coverage.categorizedTxnsCount / coverage.totalTxnsCount) * 1000
        ) / 10;

  const refresh = () => fetchPage();

  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < totalCount;
  const pager =
    totalCount > PAGE_SIZE ? (
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          Showing {(page * PAGE_SIZE + 1).toLocaleString()}–
          {Math.min((page + 1) * PAGE_SIZE, totalCount).toLocaleString()} of{" "}
          {totalCount.toLocaleString()}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!hasPrev}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-30"
          >
            ← Prev
          </button>
          <span className="text-xs">
            Page {page + 1} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
          </span>
          <button
            type="button"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      </div>
    ) : null;

  const confirm = (suggestionId: string, stakeholderId?: string) => {
    setBusyId(suggestionId);
    commitMutation<reviewPage_ConfirmSuggestionMutation>(environment, {
      mutation: confirmMutation,
      variables: { input: { suggestionId, stakeholderId } },
      onCompleted: (response) => {
        setBusyId(null);
        const errors = response.confirmSuggestion?.errors;
        if (errors && errors.length > 0) {
          setNotice(errors[0].message);
        } else {
          refresh();
        }
      },
      onError: (err) => {
        setBusyId(null);
        setNotice(err.message);
      },
    });
  };

  const reject = (suggestionId: string) => {
    setBusyId(suggestionId);
    commitMutation<reviewPage_RejectSuggestionMutation>(environment, {
      mutation: rejectMutation,
      variables: { input: { suggestionId } },
      onCompleted: () => {
        setBusyId(null);
        refresh();
      },
      onError: (err) => {
        setBusyId(null);
        setNotice(err.message);
      },
    });
  };

  const generate = () => {
    setNotice(null);
    commitMutation<reviewPage_GenerateAiSuggestionsMutation>(environment, {
      mutation: generateMutation,
      variables: {},
      onCompleted: (response) => {
        const payload = response.generateAiSuggestions;
        if (payload?.enqueued) {
          setNotice(
            "AI suggestions are being generated — refresh in a moment."
          );
        } else {
          setNotice(
            payload?.errors?.[0]?.message ?? "Could not enqueue AI suggestions"
          );
        }
      },
      onError: (err) => setNotice(err.message),
    });
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold">Categorization review</h1>
          {isFetching && (
            <span
              className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
              role="status"
              aria-label="Loading"
            />
          )}
        </div>
        <button
          type="button"
          onClick={generate}
          className="rounded bg-gray-900 text-white px-3 py-1.5 text-sm font-medium"
        >
          Generate AI suggestions
        </button>
      </div>

      <div className="bg-white rounded border p-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-medium">
            {coverage.categorizedTxnsCount.toLocaleString()} of{" "}
            {coverage.totalTxnsCount.toLocaleString()} transactions categorized
          </span>
          <span className="text-gray-500">{pct}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded overflow-hidden">
          <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {notice && (
        <p className="text-sm text-amber-700 bg-amber-50 rounded p-3">
          {notice}
        </p>
      )}

      {suggestions.length === 0 ? (
        <p className="text-gray-500 text-sm">
          No suggestions waiting for review. Use "Generate AI suggestions" to
          propose categorizations for the remaining uncategorized transactions.
        </p>
      ) : (
        <>
          {pager}
          <ul className="space-y-3">
            {suggestions.map((suggestion) => (
              <SuggestionRow
                key={suggestion.id}
                suggestion={suggestion}
                stakeholders={data.allStakeholders}
                busy={busyId === suggestion.id}
                onConfirm={confirm}
                onReject={reject}
                onChanged={refresh}
              />
            ))}
          </ul>
          {pager}
        </>
      )}

      {data.uncategorizedStakeholders.length > 0 && (
        <div className="space-y-2 pt-2">
          <h2 className="font-bold">Counterparties without a cost category</h2>
          <p className="text-sm text-gray-500">
            Assigning a category here powers the "Expenses by category" chart
            (e.g. marketing salaries under Marketing) and excludes Owner
            distributions from the profit margin.
          </p>
          <ul className="bg-white rounded border divide-y">
            {data.uncategorizedStakeholders.map(
              ({ stakeholder, totalSpend }) => (
                <StakeholderCategoryRow
                  key={stakeholder.id}
                  stakeholder={stakeholder}
                  totalSpend={totalSpend}
                  categories={data.categories}
                  onNotice={setNotice}
                />
              )
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

type CategoryOption = reviewPage_Query["response"]["categories"][number];

function StakeholderCategoryRow({
  stakeholder,
  totalSpend,
  categories,
  onNotice,
}: {
  stakeholder: { readonly id: string; readonly name: string };
  totalSpend: number;
  categories: ReadonlyArray<CategoryOption>;
  onNotice: (message: string) => void;
}) {
  const [assigned, setAssigned] = useState(false);

  const assign = (categoryId: string) => {
    if (!categoryId) return;

    commitMutation<reviewPage_AssignStakeholderCategoryMutation>(environment, {
      mutation: assignCategoryMutation,
      variables: { input: { stakeholderId: stakeholder.id, categoryId } },
      onCompleted: () => setAssigned(true),
      onError: (err) => onNotice(err.message),
    });
  };

  if (assigned) return null;

  return (
    <li className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
      <span className="truncate">
        <span className="font-medium">{stakeholder.name}</span>
        <span className="text-gray-400 ml-2">
          ${totalSpend.toLocaleString()} total spend
        </span>
      </span>
      <select
        defaultValue=""
        onChange={(e) => assign(e.target.value)}
        className="rounded border border-gray-300 text-sm py-1 px-2 max-w-56"
      >
        <option value="">Pick a category...</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.parent
              ? `${category.parent.name} › ${category.name}`
              : category.name}
          </option>
        ))}
      </select>
    </li>
  );
}

type SuggestionData =
  reviewPage_Query["response"]["pendingSuggestions"]["suggestions"][number];
type StakeholderOption =
  reviewPage_Query["response"]["allStakeholders"][number];

function SuggestionRow({
  suggestion,
  stakeholders,
  busy,
  onConfirm,
  onReject,
  onChanged,
}: {
  suggestion: SuggestionData;
  stakeholders: ReadonlyArray<StakeholderOption>;
  busy: boolean;
  onConfirm: (id: string, stakeholderId?: string) => void;
  onReject: (id: string) => void;
  onChanged: () => void;
}) {
  const [override, setOverride] = useState<string>("");
  const [showTxns, setShowTxns] = useState(false);

  return (
    <li className="bg-white rounded border p-4 space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-sm">{suggestion.pattern}</p>
          <p className="text-xs text-gray-500 mt-1">
            {suggestion.matchesCount} txns · $
            {Math.abs(suggestion.totalAmount).toLocaleString()}
            {" · "}
            <span className="uppercase">{suggestion.origin}</span>
            {suggestion.confidence != null &&
              ` · ${Math.round(suggestion.confidence * 100)}% confident`}
          </p>
          {suggestion.rationale && (
            <p className="text-xs text-gray-500 italic mt-1">
              {suggestion.rationale}
            </p>
          )}
        </div>
        <div className="text-right text-sm">
          <p className="text-gray-500 text-xs">suggested</p>
          <p className="font-medium">{suggestion.stakeholder.name}</p>
          {suggestion.category && (
            <p className="text-xs text-gray-500">{suggestion.category.name}</p>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setShowTxns(true)}
        className="text-xs text-gray-500 underline"
      >
        Show transactions
      </button>

      {showTxns && (
        <StakeholderTxnsPanel
          stakeholder={suggestion.stakeholder}
          onClose={() => setShowTxns(false)}
          onChanged={onChanged}
        />
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          disabled={busy}
          onClick={() => onConfirm(suggestion.id, override || undefined)}
          className="rounded bg-green-600 text-white px-3 py-1.5 text-sm font-medium disabled:opacity-50"
        >
          {override ? "Confirm with override" : "Confirm"}
        </button>

        <select
          value={override}
          onChange={(e) => setOverride(e.target.value)}
          className="rounded border border-gray-300 text-sm py-1.5 px-2 max-w-56"
        >
          <option value="">Correct to...</option>
          {stakeholders.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <button
          type="button"
          disabled={busy}
          onClick={() => onReject(suggestion.id)}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 disabled:opacity-50"
        >
          Reject
        </button>
      </div>
    </li>
  );
}
